// src/components/admin/performance/PerformanceFilters.js
import React, { useState, useEffect } from 'react';
import { X, RotateCcw } from 'lucide-react';
import employeeService from '../../../services/employee.service';

const PerformanceFilters = ({ filters, onFiltersChange, onClose }) => {
    const [localFilters, setLocalFilters] = useState(filters);
    const [managers, setManagers] = useState([]);
    const [departments, setDepartments] = useState([]);

    useEffect(() => {
        fetchFilterData();
    }, []);

    const fetchFilterData = async () => {
        try {
            const employees = await employeeService.getAllEmployees();
            // Get unique managers
            const uniqueManagers = employees
                .filter(emp => emp.is_manager)
                .map(emp => ({
                    id: emp.id,
                    name: `${emp.first_name} ${emp.last_name}`
                }));
            setManagers(uniqueManagers);

            // Get unique departments (you'll need to implement this)
            const uniqueDepts = [...new Set(employees.map(emp => emp.department_name).filter(Boolean))];
            setDepartments(uniqueDepts.map((name, idx) => ({ id: idx, name })));
        } catch (err) {
            console.error('Error fetching filter data:', err);
        }
    };

    const handleApply = () => {
        onFiltersChange(localFilters);
        onClose();
    };

    const handleReset = () => {
        setLocalFilters({});
        onFiltersChange({});
    };

    const handleChange = (key, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [key]: value || undefined
        }));
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
                <button
                    onClick={onClose}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                    <X className="w-5 h-5 text-gray-500" />
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Reporting Manager */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Reporting Manager
                    </label>
                    <select
                        value={localFilters.reporting_manager_id || ''}
                        onChange={(e) => handleChange('reporting_manager_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="">All Managers</option>
                        {managers.map(manager => (
                            <option key={manager.id} value={manager.id}>{manager.name}</option>
                        ))}
                    </select>
                </div>

                {/* Department */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Department
                    </label>
                    <select
                        value={localFilters.department_id || ''}
                        onChange={(e) => handleChange('department_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="">All Departments</option>
                        {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                    </select>
                </div>

                {/* Rating Range */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Min Rating
                    </label>
                    <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.5"
                        value={localFilters.min_rating || ''}
                        onChange={(e) => handleChange('min_rating', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="0"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Rating
                    </label>
                    <input
                        type="number"
                        min="0"
                        max="5"
                        step="0.5"
                        value={localFilters.max_rating || ''}
                        onChange={(e) => handleChange('max_rating', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder="5"
                    />
                </div>

                {/* Review Type */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Review Type
                    </label>
                    <select
                        value={localFilters.review_type || ''}
                        onChange={(e) => handleChange('review_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="">All Types</option>
                        <option value="manager_review">Manager Review</option>
                        <option value="self_review">Self Review</option>
                    </select>
                </div>

                {/* Date Range */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Start Date
                    </label>
                    <input
                        type="date"
                        value={localFilters.start_date || ''}
                        onChange={(e) => handleChange('start_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        End Date
                    </label>
                    <input
                        type="date"
                        value={localFilters.end_date || ''}
                        onChange={(e) => handleChange('end_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                </div>

                {/* Sort Options */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sort By
                    </label>
                    <select
                        value={localFilters.sort_by || 'created_at'}
                        onChange={(e) => handleChange('sort_by', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="created_at">Date Created</option>
                        <option value="overall_rating">Rating</option>
                        <option value="review_period_start">Review Period Start</option>
                        <option value="review_period_end">Review Period End</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sort Order
                    </label>
                    <select
                        value={localFilters.sort_order || 'DESC'}
                        onChange={(e) => handleChange('sort_order', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                        <option value="DESC">Descending</option>
                        <option value="ASC">Ascending</option>
                    </select>
                </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                    onClick={handleReset}
                    className="inline-flex items-center px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Reset
                </button>
                <button
                    onClick={handleApply}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Apply Filters
                </button>
            </div>
        </div>
    );
};

export default PerformanceFilters;
